import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/cn';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface ComboboxItem {
  value?: string;
  /** Alias of `value` (used by legacy/list call sites). */
  id?: string;
  label: string;
  description?: string;
  /** Alias of `description`. */
  sublabel?: string;
  icon?: ReactNode;
  badge?: string;
  group?: string;
  /** Optional URL — if set, selecting the item navigates there. */
  href?: string;
}

interface ComboboxProps {
  items: ComboboxItem[];
  value?: string;
  onSelect?: (value: string) => void;
  /** Fires on each query input change (for live-filter parent components). */
  onQueryChange?: (q: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  /** Mode modal full-screen (cmd-K) ou inline */
  mode?: 'inline' | 'modal';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Combobox — wrappeur cmdk.
 * Modes : inline (glissé dans un formulaire) ou modal (cmd-K overlay).
 */
export function Combobox({
  items,
  value,
  onSelect,
  onQueryChange,
  placeholder: _placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  emptyMessage = 'Aucun résultat.',
  className,
  mode = 'inline',
  open = true,
  onOpenChange,
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const itemValue = (item: ComboboxItem): string => item.value ?? item.id ?? '';
  const itemDescription = (item: ComboboxItem): string | undefined =>
    item.description ?? item.sublabel;

  useEffect(() => {
    if (mode !== 'modal' || !open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const root = dialogRef.current;
    const initial = root?.querySelector<HTMLElement>('input, button, [cmdk-input]');
    initial?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange?.(false);
        return;
      }
      if (e.key !== 'Tab' || !root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
      );
      if (focusables.length === 0) return;
      const first = focusables.at(0);
      const last = focusables.at(-1);
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mode, open, onOpenChange]);

  const filtered = query
    ? items.filter(
        (item) =>
          item.label.toLowerCase().includes(query.toLowerCase()) ||
          itemDescription(item)?.toLowerCase().includes(query.toLowerCase()),
      )
    : items;

  const groups = Array.from(new Set(filtered.map((i) => i.group ?? ''))).filter(Boolean);
  const ungrouped = filtered.filter((i) => !i.group);

  const handleQuery = (q: string) => {
    setQuery(q);
    onQueryChange?.(q);
  };

  const handleSelect = (val: string) => {
    const target = items.find((i) => itemValue(i) === val);
    if (target?.href && typeof window !== 'undefined') {
      window.location.href = target.href;
      return;
    }
    onSelect?.(val);
    onOpenChange?.(false);
  };

  const content = (
    <Command
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border border-stone-200/70 bg-paper shadow-floating',
        mode === 'modal' && 'max-h-[480px] w-full max-w-lg',
        className,
      )}
      shouldFilter={false}
    >
      {/* Search */}
      <div className="flex items-center gap-2 border-b border-stone-200/70 px-3.5 py-3">
        <Search className="h-4 w-4 shrink-0 text-stone-400" strokeWidth={1.5} />
        <Command.Input
          value={query}
          onValueChange={handleQuery}
          placeholder={searchPlaceholder}
          className="flex-1 bg-transparent text-sm text-ink placeholder:text-stone-400 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => handleQuery('')}
            aria-label="Effacer"
            className="text-stone-400 transition-colors hover:text-ink"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* List */}
      <Command.List className="overflow-y-auto overscroll-contain p-1.5">
        <Command.Empty className="py-6 text-center text-sm text-stone-500">
          {emptyMessage}
        </Command.Empty>

        {/* Grouped items */}
        {groups.map((group) => (
          <Command.Group
            key={group}
            heading={group}
            className="[&>[cmdk-group-heading]]:px-2.5 [&>[cmdk-group-heading]]:py-1.5 [&>[cmdk-group-heading]]:text-[10px] [&>[cmdk-group-heading]]:font-semibold [&>[cmdk-group-heading]]:uppercase [&>[cmdk-group-heading]]:tracking-[0.12em] [&>[cmdk-group-heading]]:text-stone-400"
          >
            {filtered
              .filter((i) => i.group === group)
              .map((item) => {
                const val = itemValue(item);
                return (
                  <ComboboxItemRow
                    key={val}
                    item={item}
                    itemValue={val}
                    itemDescription={itemDescription(item)}
                    isSelected={val === value}
                    onSelect={handleSelect}
                  />
                );
              })}
          </Command.Group>
        ))}

        {/* Ungrouped items */}
        {ungrouped.map((item) => {
          const val = itemValue(item);
          return (
            <ComboboxItemRow
              key={val}
              item={item}
              itemValue={val}
              itemDescription={itemDescription(item)}
              isSelected={val === value}
              onSelect={handleSelect}
            />
          );
        })}
      </Command.List>
    </Command>
  );

  if (mode === 'modal') {
    if (!open) return null;
    return (
      // Backdrop interactif (clic-pour-fermer + Échap). Le rôle "dialog"
      // n'accepte pas les listeners de souris/clavier au sens strict
      // (jsx-a11y/no-noninteractive-element-interactions), mais
      // sémantiquement c'est bien un dialog modal — on neutralise la
      // règle ici car le pattern (Stripe Cmd-K, Linear Search) est
      // attendu par les utilisateurs et accessible au clavier (Échap).
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Recherche"
        className="fixed inset-0 z-[60] flex items-start justify-center bg-ink/40 px-4 pt-[12vh] backdrop-blur-sm"
        onClick={(e) => {
          if (e.target === e.currentTarget) onOpenChange?.(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onOpenChange?.(false);
        }}
      >
        {content}
      </div>
    );
  }

  return content;
}

function ComboboxItemRow({
  item,
  itemValue,
  itemDescription,
  isSelected,
  onSelect,
}: {
  item: ComboboxItem;
  itemValue: string;
  itemDescription: string | undefined;
  isSelected: boolean;
  onSelect: (v: string) => void;
}) {
  return (
    <Command.Item
      value={itemValue}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-stone-700 transition-colors duration-75 hover:bg-stone-50 hover:text-ink aria-selected:bg-stone-100 aria-selected:text-ink',
        isSelected && 'font-medium text-ink',
      )}
    >
      {item.icon && <span className="shrink-0 text-stone-400">{item.icon}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{item.label}</span>
        {itemDescription && (
          <span className="block truncate text-[11px] text-stone-400">{itemDescription}</span>
        )}
      </span>
      {item.badge && (
        <span className="shrink-0 rounded-md bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-500">
          {item.badge}
        </span>
      )}
      {isSelected && (
        <svg
          className="h-4 w-4 shrink-0 text-brand-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </Command.Item>
  );
}
